import type { InboxListParams } from '../api/conversations-api';

/**
 * The inbox list and counts are organisation-wide; a conversation and its thread live under their
 * store, so switching stores never shares a thread's cache.
 */
export const conversationKeys = {
  all: ['conversations'] as const,
  lists: () => [...conversationKeys.all, 'list'] as const,
  list: (params: InboxListParams) => [...conversationKeys.lists(), params] as const,
  counts: () => [...conversationKeys.all, 'counts'] as const,
  store: (storeId: string) => ['stores', storeId, 'conversations'] as const,
  detail: (storeId: string, id: string) =>
    [...conversationKeys.store(storeId), 'detail', id] as const,
  messages: (storeId: string, id: string) =>
    [...conversationKeys.detail(storeId, id), 'messages'] as const,
  context: (storeId: string, id: string) =>
    [...conversationKeys.detail(storeId, id), 'context'] as const,
  shortcuts: (storeId: string) => [...conversationKeys.store(storeId), 'shortcuts'] as const,
  departments: (storeId: string) => [...conversationKeys.store(storeId), 'departments'] as const,
  presence: (storeId: string) => [...conversationKeys.store(storeId), 'presence'] as const,
  /** Staff who can work this store's chat (LCW1). */
  agents: (storeId: string) => [...conversationKeys.store(storeId), 'agents'] as const,
  /** Discounts the composer can hand out, with the member's daily allowance (LC12). */
  discounts: (storeId: string) => [...conversationKeys.store(storeId), 'discounts'] as const,
  /** Live help articles for the composer's `?` search (LC8). */
  helpArticles: (storeId: string, q: string) =>
    [...conversationKeys.store(storeId), 'help-articles', q] as const,
  /** The live visitor list of one store (LC7). */
  visitors: (storeId: string) => [...conversationKeys.store(storeId), 'visitors'] as const,
  /** One visitor's profile and visit, for the details sheet. */
  visitor: (storeId: string, id: string) =>
    [...conversationKeys.store(storeId), 'visitor', id] as const,
  /** Every org-wide live visitor list (LCW1), whatever store it is narrowed to. */
  orgVisitorLists: () => [...conversationKeys.all, 'visitors'] as const,
  /** The org-wide live visitor list, narrowed to one store or not (`null`). */
  orgVisitors: (storeId: string | null) =>
    [...conversationKeys.orgVisitorLists(), storeId] as const,
};

/**
 * Whether an org-wide visitor list key covers a store: the unnarrowed list covers every store,
 * a narrowed one only its own.
 */
export const orgVisitorsCover = (key: readonly unknown[], storeId: string): boolean =>
  key[0] === 'conversations' && key[1] === 'visitors' && (key[2] === null || key[2] === storeId);
